import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import ProductCard5 from "../Products/ProductCard5";
import { XMasonry, XBlock } from "react-xmasonry";
import { useInView } from "react-intersection-observer";
import { UserAuth } from "../Context";

import RecentlyOnlineUsers from "../components/RecentlyOnlineUsers";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
  where,
} from "firebase/firestore";
import { db } from "../FirebaseConfig";
import useLogsStore from "../StatesStore/Logs";
import useUserDataStore from "../StatesStore/UserData";
import Skeleton from "../Products/Skeleton";

import RecentlyJoinedUsers from "../components/RecentlyJoinedUsers";
import SearchStyle from "../Cards/SearchStyle";
import ProductCard3 from "../Products/ProductCard3";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import useSocketStore from "../StatesStore/Socket";
import SideNavUserProfile from "../SideNav/SideNavUserProfile";

import ProductCard6 from "../Products/ProductCard6";
import ProductCard7 from "../Products/ProductCard7";
import ShoppingListPostCard from "../UserPosts/ShopPost/ShoppingListPostCard";

export default function ImagePopup() {
  const { ref: loadMoreRef, inView: loadMoreView } = useInView({
    threshold: 0.1,
  });

  const { user } = UserAuth();
  const logs = useLogsStore((state) => state.logs);
  const userData = useUserDataStore((state) => state.userData);

  const [productIds, setProductIds] = useState(null);
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [shoppingListPosts, setShoppingListPosts] = useState([]);

  // Extract user's favorite subcategories and sub-subcategories
  const getFavoriteCategories = () => {
    if (!userData?.favCategories) return { subCategories: [], subSubCategories: [] };
    
    const subCategories = [];
    const subSubCategories = [];
    
    userData.favCategories.forEach(category => {
      if (category.subCategory) subCategories.push(category.subCategory);
      if (category.subSubCategory) subSubCategories.push(category.subSubCategory);
    });
    
    return {
      subCategories: [...new Set(subCategories)], // Remove duplicates
      subSubCategories: [...new Set(subSubCategories)] // Remove duplicates
    };
  };

  // Fetch product IDs from logs or user preferences
  const fetchProductIdsFromLogs = async () => {
    if (logs && logs.length > 0) return logs;
    const ids = [];
    try {
      const logsRef = collection(db, "users", user?.uid, "logs");
      const logsQuery = query(
        logsRef,
        where("productId", "!=", null),
        orderBy("timestamp", "desc"),
        limit(10)
      );
      const logsSnapshot = await getDocs(logsQuery);

      logsSnapshot.forEach((doc) => {
        const logData = doc.data();
        if (logData.productId) ids.push(logData.productId);
      });

      return ids;
    } catch (error) {
      console.error("Error fetching productIds from logs:", error);
      return [];
    }
  };

  // Fetch shopping list posts in parallel
  const fetchShoppingListPosts = async ({ pageParam = null }) => {
    try {
      const { subSubCategories } = getFavoriteCategories();
      
      let postsQuery = query(
        collection(db, "posts"),
        where("postType", "==", "shoppingList"),
        orderBy("dateCreated", "desc"),
        limit(1)
      );

      if (subSubCategories.length > 0) {
        postsQuery = query(
          postsQuery,
          where("subSubCategory", "array-contains-any", subSubCategories)
        );
      }

      if (pageParam) {
        postsQuery = query(postsQuery, startAfter(pageParam));
      }

      const postsSnapshot = await getDocs(postsQuery);
      const posts = postsSnapshot.docs.map(doc => ({
        postId: doc.id,
        ...doc.data(),
        isShoppingList: true
      }));
      
      return {
        posts,
        lastVisible: postsSnapshot.docs[postsSnapshot.docs.length - 1]
      };
    } catch (error) {
      console.error("Error fetching shopping list posts:", error);
      return { posts: [], lastVisible: null };
    }
  };
  const {
    data: shoppingPosts,
    fetchNextPage: fetchNextShoppingPosts,
    hasNextPage: hasNextShoppingPage,
    isFetchingNextPage: isFetchingNextShoppingPage
  } = useInfiniteQuery({
    queryKey: ['shoppingPosts', user?.uid],
    queryFn: fetchShoppingListPosts,
    getNextPageParam: (lastPage) => lastPage.lastVisible || undefined,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    enabled: !!userData,
  });
  
  const {
    data: recommendations,
    error,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
    isFetching
  } = useInfiniteQuery({
    queryKey: ['products'],
    queryFn: async ({ pageParam = 1 }) => {
      const [recommendations, shoppingPosts] = await Promise.all([
        axios.post(`https://recommender-1046242076270.asia-northeast2.run.app/api/recommend/${user?.uid}`, {
          product_ids: productIds,
          page_number: pageParam,
          items_per_page: 25,
          favCategories: userData?.favCategories
        }),
        // fetchShoppingListPosts()
      ]);
      
      // setShoppingListPosts(shoppingPosts);
      return recommendations.data;
    },
    enabled: productIds !== null,
    staleTime: Infinity, 
    cacheTime: Infinity, 
    refetchOnWindowFocus: false, 
    refetchOnMount: false,
    refetchInterval: false,
    placeholderData: (previousData) => {
      if (previousData) return previousData;

      return {
        pages: [Array.from({ length: 25 }, (_, index) => ({
          id: `skeleton-${index}`,
          isSkeleton: true,
          name: "Loading...",
        }))],
        pageParams: [1]
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      return lastPage?.length < 15 ? undefined : allPages?.length + 1;
    },
  });

  // Load product IDs on component mount
  useEffect(() => {
    const initializeProductIds = async () => {
      if (!user) return;

      const ids = await fetchProductIdsFromLogs();
      setProductIds(ids);
    };

    initializeProductIds();
  }, [user]);

  useEffect(() => {
    if (loadMoreView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
    if (hasNextShoppingPage && !isFetchingNextShoppingPage) {
      fetchNextShoppingPosts();
    }
  }, [loadMoreView]);

  function getTargetBlockWidth() {
    const screenWidth = window.innerWidth;
    return screenWidth < 440 ? 200 : 250;
  }

  // Combine recommended products with shopping list posts
  const allShoppingPosts = shoppingPosts?.pages?.flatMap(page => page.posts) || [];
  const allRecommendations = recommendations?.pages?.flat() || [];
  const combinedData = [...allShoppingPosts, ...allRecommendations];

  const isMobileScreen = window.innerWidth <= 768;
  return (
    <>
      <div className="vstack gap-4 px-0">
        <XMasonry
          center={true}
          responsive={true}
          maxColumns={5}
          targetBlockWidth={getTargetBlockWidth()}
        >
          {combinedData?.map((item, index) => {
          
          
            const isSecondLast = index === combinedData.length - 15;
            
            // if (item?.isShoppingList) {
            //   // console.log('isShoppingList',item);
              
            //   // Render shopping list posts differently if needed
            //   return (
            //     <XBlock key={`shopping-${item.postId + index}`} width={2}>
            //       <div className={`me-0 ${index === 0 ? "ms-1" : "ms-2"}`} >
            //         <ShoppingListPostCard post={item}/>
            //       </div>
            //     </XBlock>
            //   );
            // }
            
            return (
              <XBlock key={item.id + index} width={1}>
                {item.isSkeleton ? (
                  <Skeleton />
                ) : (
                  <div className={`me-0 ${index === 0 ? "ms-1" : "ms-2"}`} ref={isSecondLast ? loadMoreRef : null}>
                    <ProductCard7 product={item} />
                  </div>
                )}
              </XBlock>
            );
          })}
          {/* {isLoading &&
            Array.from({ length: 1 }).map((_, index) => (
              <XBlock key={`skeleton-${index}`} width={1}>
                <Skeleton />
              </XBlock>
            ))} */}
        </XMasonry>

        {isLoading && (
          <div className="d-flex justify-content-center align-items-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        )}
      </div>
    </>
  );
}